import { useState, useCallback, useMemo, useEffect } from 'react';
import { ParameterBar, GroupingSelector } from '@/components/forms';
import { ChartModal } from '@/components/modals';
import { GroupedTable } from '@/components/tables/GroupedTable';
import { useCalculation } from '@/hooks/useCalculation';
import { useUserState } from '@/hooks/useUserState';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useAutoCalculate } from '@/hooks/useAutoCalculate';
import { useGroupOrder } from '@/hooks/useGroupOrder';
import { useGroupedData } from '@/hooks/useGroupedData';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { GroupingMode } from '@/types/grouping';

// Default date: 5 years ago to today
function getDefaultStartDate(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 5);
  return date.toISOString().split('T')[0];
}

function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

interface ShowBySettings {
  initialInvestment: number;
  adjustInflation: boolean;
}

const DEFAULT_SETTINGS: ShowBySettings = {
  initialInvestment: 10000,
  adjustInflation: false,
};

/**
 * Show By page component
 * Similar to Calculator but with advanced grouping options
 */
export function ShowBy() {
  // Persisted state (synced to Supabase for authenticated users)
  const {
    tickers,
    setTickers,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    settings: calculatorSettings,
    setSettings: setCalculatorSettings,
    visibleColumns,
    setVisibleColumns,
    groupOrder,
    setGroupOrder,
  } = useUserState({ debounceMs: 1000 });

  // Convert calculator settings to show by settings (subset)
  const settings: ShowBySettings = useMemo(
    () => ({
      initialInvestment: calculatorSettings.initialInvestment,
      adjustInflation: calculatorSettings.adjustInflation,
    }),
    [calculatorSettings]
  );

  const setSettings = useCallback(
    (updater: (prev: ShowBySettings) => ShowBySettings) => {
      setCalculatorSettings((prev) => {
        const newSettings = updater(settings);
        return { ...prev, ...newSettings };
      });
    },
    [setCalculatorSettings, settings]
  );

  // Grouping mode - persisted separately
  const [groupingMode, setGroupingMode] = useLocalStorage<GroupingMode>(
    'showby_grouping_mode',
    'sector'
  );

  // Column visibility with external state from useUserState
  const { toggleColumn, toggleAll, allColumns } = useColumnVisibility({
    visibleColumns,
    setVisibleColumns,
  });

  // Column group ordering with external state from useUserState
  // reorderGroups available for future drag-and-drop functionality
  useGroupOrder({
    groupOrder,
    setGroupOrder,
  });

  // Modal state
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);

  // Calculation hook
  const {
    loading,
    loadingTickers,
    error,
    data,
    calculateSingle,
    calculateBatch,
    removeTickerData,
    reset,
  } = useCalculation();

  // Group the data based on selected mode
  const groupedData = useGroupedData(data?.tableData ?? [], groupingMode);

  // Perform calculation - uses batch endpoint for efficiency
  const doCalculate = useCallback(() => {
    if (tickers.length === 0) return;

    // Clear existing data when parameters change
    reset();

    // Use batch endpoint to send all tickers in one request
    calculateBatch({
      tickers,
      startDate,
      endDate,
      reinvestDividends: true,
      adjustInflation: settings.adjustInflation,
      initialInvestment: settings.initialInvestment,
    });
  }, [tickers, startDate, endDate, settings, calculateBatch, reset]);

  // Auto-calculate when parameters change (debounced)
  useAutoCalculate(
    {
      tickers,
      startDate,
      endDate,
      initialInvestment: settings.initialInvestment,
      adjustInflation: settings.adjustInflation,
      enabled: tickers.length > 0,
      debounceMs: 600,
    },
    doCalculate
  );

  // Calculate on initial load if tickers exist
  useEffect(() => {
    if (tickers.length > 0 && !data && !loading) {
      calculateBatch({
        tickers,
        startDate,
        endDate,
        reinvestDividends: true,
        adjustInflation: settings.adjustInflation,
        initialInvestment: settings.initialInvestment,
      });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle ticker add callback
  const handleTickerAdd = useCallback(
    (ticker: string) => {
      calculateSingle({
        ticker,
        startDate,
        endDate,
        reinvestDividends: true,
        adjustInflation: settings.adjustInflation,
        initialInvestment: settings.initialInvestment,
      });
    },
    [calculateSingle, startDate, endDate, settings.adjustInflation, settings.initialInvestment]
  );

  // Handle ticker removal
  const handleRemoveTicker = useCallback(
    (ticker: string) => {
      setTickers((prev) => prev.filter((t) => t !== ticker));
      removeTickerData(ticker);
    },
    [setTickers, removeTickerData]
  );

  // Handle reset
  const handleReset = useCallback(() => {
    setTickers([]);
    setStartDate(getDefaultStartDate());
    setEndDate(getTodayDate());
    setSettings(() => DEFAULT_SETTINGS);
    reset();
  }, [setTickers, setStartDate, setEndDate, setSettings, reset]);

  // Handle row click to open chart modal
  const handleRowClick = useCallback((ticker: string) => {
    setSelectedTicker(ticker);
    setIsChartModalOpen(true);
  }, []);

  // Get chart data for selected ticker
  const selectedChartData = useMemo(() => {
    if (!selectedTicker || !data) return null;
    return data.chartData.find((d) => d.ticker === selectedTicker) || null;
  }, [selectedTicker, data?.chartData]);

  // Determine which grouping modes are available based on data
  const availableModes = useMemo<GroupingMode[]>(() => {
    const modes: GroupingMode[] = ['ticker', 'performance', 'dividend', 'assetType'];

    if (data?.tableData) {
      // Check if any tickers have underlying
      if (data.tableData.some((t) => t.underlyingTicker)) {
        modes.push('underlying');
      }
      // Check if any tickers have sector
      if (data.tableData.some((t) => t.sector)) {
        modes.push('sector');
      }
      // Check if any tickers have industry
      if (data.tableData.some((t) => t.industry)) {
        modes.push('industry');
      }
    }

    return modes;
  }, [data?.tableData]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Parameter Bar */}
      <div className="px-4 py-4">
        <ParameterBar
          tickers={tickers}
          onTickersChange={setTickers}
          onTickerAdd={handleTickerAdd}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          initialInvestment={settings.initialInvestment}
          onInitialInvestmentChange={(amount) =>
            setSettings((prev) => ({ ...prev, initialInvestment: amount }))
          }
          adjustInflation={settings.adjustInflation}
          onAdjustInflationChange={(value) =>
            setSettings((prev) => ({ ...prev, adjustInflation: value }))
          }
          groupByUnderlying={false}
          onGroupByUnderlyingChange={() => {}}
          onReset={handleReset}
          isCalculating={loading}
          columns={allColumns}
          visibleColumns={visibleColumns}
          onColumnToggle={toggleColumn}
          onColumnToggleAll={toggleAll}
          hideGroupByUnderlying
        />
      </div>

      {/* Grouping Selector */}
      <div className="px-4 pb-4">
        <GroupingSelector
          mode={groupingMode}
          onChange={setGroupingMode}
          availableModes={availableModes}
          disabled={loading || !data}
        />
      </div>

      {/* Main Content Area */}
      <main className="flex-1 px-4 pb-4">
        {/* Error Message */}
        {error && (
          <div
            className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
            role="alert"
          >
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div
            className="flex items-center justify-center py-12"
            role="status"
            aria-label="Calculating returns"
          >
            <svg
              className="animate-spin h-8 w-8 text-primary mr-3"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-muted-foreground">
              Calculating returns
              {tickers.length > 0 ? ` for ${tickers.join(', ')}` : ''}...
            </span>
          </div>
        )}

        {/* Results Table - show if we have data OR loading tickers */}
        {(data || loadingTickers.length > 0) && !loading && (
          <GroupedTable
            groups={groupedData}
            onRowClick={handleRowClick}
            visibleColumns={visibleColumns}
            onRemoveTicker={handleRemoveTicker}
            loadingTickers={loadingTickers}
            groupOrder={groupOrder}
          />
        )}

        {/* Empty State */}
        {!data && !loading && !error && loadingTickers.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <p className="text-muted-foreground text-center">
              Type a ticker symbol and press Enter to see returns grouped by your selected criteria.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border px-4 py-3">
        <p className="text-center text-xs text-muted-foreground">
          Data provided for informational purposes only. Past performance does not guarantee future
          results.
        </p>
      </footer>

      {/* Chart Modal */}
      <ChartModal
        isOpen={isChartModalOpen}
        onClose={() => {
          setIsChartModalOpen(false);
          setSelectedTicker(null);
        }}
        ticker={selectedTicker || ''}
        chartData={selectedChartData}
        initialInvestment={settings.initialInvestment}
      />
    </div>
  );
}

export default ShowBy;
